import styled from "styled-components";
import colors from "../../shared/constants/color";
import { Link, NavLink } from "react-router-dom";

interface SidebarContainerProps {
  isOpen: boolean;
}

export const MenuContentWrapper = styled.span`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

export const SidebarContainer = styled.div<SidebarContainerProps>`
  height: calc(100vh - 64px);
  background-color: ${colors.neutral.N1000};
  border-right: 1px solid ${colors.neutral.N1100};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 64px;
  z-index: 1000;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: ${(props) => (props.isOpen ? "250px" : "65px")};
  transition: width 0.3s ease-in-out, background-color 0.2s ease, border-color 0.2s ease;
  overflow: visible;

  [data-theme="dark"] & {
    background-color: var(--w-sidebar-bg);
    border-right-color: var(--w-border);
    box-shadow: none;
  }

  ${(props) => `
    ${MenuContentWrapper} {
      transition: max-width 0.3s ease, opacity 0.2s ease, transform 0.3s ease;
      display: flex;
      align-items: center;
      min-width: 0;
      ${
        props.isOpen
          ? `
        max-width: 180px;
        opacity: 1;
        transform: translateX(0);
      `
          : `
        max-width: 0px;
        opacity: 0;
        transform: translateX(-8px);
      `
      }
    }
  `}
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 25px 0px;
  margin-top: 26px;
  position: relative;
`;

export const UserInfo = styled.div`
  display: flex;
  margin-left: 20px;
  width: 100%;
  align-items: center;
`;

export const UserInfoCollapsed = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const UserDetails = styled.div<{ isOpen?: boolean }>`
  margin-left: 0px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 35px;
  transition: max-width 0.3s ease, opacity 0.2s ease, transform 0.3s ease;
  max-width: ${(props) => (props.isOpen ? "200px" : "0")};
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  transform: ${(props) => (props.isOpen ? "translateX(0)" : "translateX(-8px)")};
  overflow: hidden;
`;

export const UserName = styled.h3`
  font-family: "Inter", sans-serif;
  margin-left: 13px;
  font-size: 14px;
  color: ${colors.neutral.N500};
  width: auto;
  height: 18px;
  line-height: 20px;
  font-weight: 500;
  font-style: normal;
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  margin-right: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  [data-theme="dark"] & {
    color: var(--w-text-main);
  }
`;

export const UserRole = styled.p`
  font-family: "Inter", sans-serif;
  margin-left: 13px;
  font-size: 12px;
  color: ${colors.neutral.N500};
  width: auto;
  height: 17px;
  line-height: 17px;
  letter-spacing: 0%;
  font-weight: 400;
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  margin-right: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  [data-theme="dark"] & {
    color: var(--w-text-muted);
  }
`;

export const ToggleContainer = styled.div`
  position: absolute;
  right: -20px;
  top: 75px;
  transform: translateY(-50%);
`;

export const ToggleButton = styled.button`
  background-color: white;
  border: 1px solid ${colors.neutral.N1100};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  padding: 0;

  [data-theme="dark"] & {
    background-color: var(--w-bg-soft);
    border-color: var(--w-border);
    box-shadow: none;
  }
`;

export const SidebarMenu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  flex: 1 1 auto;
`;

export const MenuList = styled.ul`
  font-weight: 400;
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: hidden;
  overflow-x: hidden;
  flex: 1 1 auto;
`;

export const MenuItem = styled.li`
  margin-bottom: 0;
  position: relative;
  overflow: visible;

  &.active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${colors.deepBlue};
    border-radius: 0 4px 4px 0;
    z-index: 2;
  }

  [data-theme="dark"] &.active::before {
    background: var(--w-accent);
  }
`;

export const MenuLink = styled(Link)`
  display: flex;
  flex-direction: row;
  height: 55px;
  align-items: center;
  padding: 12px 15px;
  color: ${colors.neutral.N600};
  text-decoration: none;
  transition: all 0.3s ease;
  border-bottom: 1px solid ${colors.neutral.N0};
  gap: 8px;
  flex-wrap: nowrap;
  width: 250px;

  &:hover {
    background-color: ${colors.neutral.N1100};
    color: ${colors.gray900};
  }

  [data-theme="dark"] & {
    color: var(--w-text-soft);
    border-bottom-color: var(--w-border-subtle);

    &:hover {
      background-color: var(--w-nav-hover-bg);
      color: var(--w-text-main);
    }
  }

  [data-theme="dark"] li.active & {
    background-color: var(--w-nav-hover-bg);
    color: var(--w-accent);
  }
`;

export const MenuIcon = styled.span`
  font-size: 20px !important;
  min-width: 25px;
  width: 25px;
  max-width: 25px;
  height: 25px;
  color: ${colors.gray800};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: none !important;
  opacity: 1 !important;
  transform: none !important;

  [data-theme="dark"] & {
    color: var(--w-text-muted);
  }
`;

export const MenuItemWithSubmenu = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MenuItemButton = styled.div.attrs({
  className: "wiser-sidebar-menu-button",
})`
  display: flex;
  height: 55px;
  align-items: center;
  padding: 12px 15px;
  color: ${colors.gray800};
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid ${colors.neutral.N0};
  width: 250px;
  gap: 8px;
  flex-wrap: nowrap;

  &:hover {
    background-color: ${colors.neutral.N1100};
    color: ${colors.gray900};
  }

  li.active & {
    background-color: ${colors.neutral.N1100};
    color: ${colors.neutral.N600};
  }

  [data-theme="dark"] & {
    color: var(--w-text-soft);
    border-bottom-color: var(--w-border-subtle);

    &:hover {
      background-color: var(--w-nav-hover-bg);
      color: var(--w-text-main);
    }
  }

  [data-theme="dark"] li.active & {
    background-color: var(--w-nav-hover-bg);
    color: var(--w-accent);
    box-shadow: 0 10px 20px rgba(46, 88, 166, 0.35);
  }
`;

interface SubmenuProps {
  expanded: boolean;
}

export const Submenu = styled.ul<SubmenuProps>`
  list-style: none;
  margin: 0;
  padding-left: 0;
  overflow: hidden;
  max-height: ${(props) => (props.expanded ? "500px" : "0")};
  visibility: ${(props) => (props.expanded ? "visible" : "hidden")};
  transition: max-height 0.3s ease, opacity 0.25s ease, transform 0.3s ease;
`;

export const SubmenuItem = styled.li`
  margin-bottom: 0;
`;

export const SubmenuLink = styled(NavLink).attrs({
  className: "wiser-sidebar-submenu-link",
})`
  padding: 8px 15px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  color: ${colors.neutral.N200};
  text-decoration: none;
  transition: all 0.3s ease;
  width: 250px;
  height: 40px;

  &:hover {
    background-color: ${colors.neutral.N1100};
    color: ${colors.gray900};
  }

  &.active {
    color: ${colors.blue.B500};
    font-weight: 600;
  }

  [data-theme="dark"] & {
    color: var(--w-text-soft);

    &:hover {
      background-color: var(--w-nav-hover-bg);
      color: var(--w-text-main);
    }

    &.active {
      color: var(--w-accent);
      font-weight: 600;
    }
  }
`;

export const SubmenuLabel = styled.span`
  padding-left: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  display: block;
`;

export const MenuLabel = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  color:${colors.neutral.N600};
  flex: 1;
  width: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  li.active:not(.no-active-typography) & {
    color: ${colors.blue.B500};
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }
`;
export const SidebarFooter = styled.div.attrs({
  className: "wiser-sidebar-footer",
})`
  flex-shrink: 0;
  margin-top: auto;
  border-top: 1px solid ${colors.neutral.N0};
  width: 100%;
  overflow: hidden;

  [data-theme="dark"] & {
    border-top-color: var(--w-border);
  }
`;

/* Dark mode tema anahtari (upstream 1e14074). DarkModeToggle bu uc bileseni kullaniyor. */
export const SidebarThemeButton = styled.button<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  width: ${({ $compact }) => ($compact ? "250px" : "100%")};
  height: 55px;
  padding: 12px 15px;
  gap: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  justify-content: flex-start;
  color: ${colors.neutral.N600};

  &:hover {
    background-color: ${colors.neutral.N1100};
  }

  [data-theme="dark"] & {
    color: var(--w-text-soft);

    &:hover {
      background-color: var(--w-nav-hover-bg);
      color: var(--w-text-main);
    }
  }
`;

export const SidebarThemeIcon = styled.span`
  min-width: 25px;
  width: 25px;
  max-width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.gray800};

  .MuiSvgIcon-root {
    font-size: 20px;
  }

  [data-theme="dark"] & {
    color: var(--w-text-muted);
  }
`;

export const SidebarThemeLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.neutral.N600};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  [data-theme="dark"] & {
    color: var(--w-text-soft);
  }
`;
